/**
 * Instellingenblok "Dit apparaat" (08-09) — schakelaars die bij déze
 * browser horen, niet bij het account, net als het push-abonnement
 * eronder. De stand zit in localStorage; een ander toestel heeft dus
 * zijn eigen keuze.
 *
 * Sessie 4 (09-09): het geluid heeft een volumeschuif gekregen en drie
 * voorbeeldknoppen. De tonen worden opgewekt (zie
 * `@/lib/meldingsgeluid`), dus het volume is een echte versterking en
 * niet de vaste luidheid van een opgenomen bestand. Elke klik hier is
 * bovendien de gebruikersinteractie die het browserbeleid vraagt
 * voordat er überhaupt geluid mag klinken.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Laptop, Volume2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  haalPortaalVolume,
  isPortaalGeluidAan,
  isVertrouwdApparaat,
  zetPortaalGeluid,
  zetPortaalVolume,
  zetVertrouwdApparaat,
} from "@/lib/apparaatInstellingen";
import { meldGebruikersinteractie, speelToon } from "@/lib/meldingsgeluid";
import type { ToonSoort } from "@/lib/meldingsgeluid";

/** De drie tonen, in de volgorde waarin ze op het scherm staan. */
const VOORBEELDEN: readonly ToonSoort[] = [
  "nieuw",
  "overdracht",
  "klant_wacht",
];

export function ApparaatInstellingen() {
  const { t } = useTranslation();
  const [vertrouwd, setVertrouwd] = useState(isVertrouwdApparaat);
  const [geluid, setGeluid] = useState(isPortaalGeluidAan);
  const [volume, setVolume] = useState(haalPortaalVolume);

  /**
   * Laat een toon horen op de huidige schuifstand. Bewust niet via
   * `speelMeldingsgeluid`: die ontdubbelt twee meldingen binnen twee
   * seconden, en juist hier wil je de drie tonen achter elkaar kunnen
   * vergelijken.
   */
  function beluister(soort: ToonSoort, luidheid = volume) {
    meldGebruikersinteractie();
    speelToon(soort, luidheid / 100);
  }

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-brand-cyan-soft text-brand-cyan-dark grid place-items-center">
            <Laptop className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h2 className="text-[15px] font-semibold">
              {t("profiel.apparaat_titel")}
            </h2>
            <p className="text-[13px] text-muted-foreground mt-0.5">
              {t("profiel.apparaat_uitleg")}
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <Label
                htmlFor="vertrouwd-apparaat"
                className="text-[13.5px] font-medium"
              >
                {t("profiel.vertrouwd_apparaat")}
              </Label>
              <p className="text-[12.5px] text-muted-foreground mt-0.5">
                {t("profiel.vertrouwd_apparaat_uitleg")}
              </p>
            </div>
            <Switch
              id="vertrouwd-apparaat"
              checked={vertrouwd}
              onCheckedChange={(aan) => {
                setVertrouwd(aan);
                zetVertrouwdApparaat(aan);
              }}
            />
          </div>

          <div className="flex items-start justify-between gap-4 border-t pt-3">
            <div className="flex-1">
              <Label
                htmlFor="portaal-geluid"
                className="text-[13.5px] font-medium inline-flex items-center gap-1.5"
              >
                <Volume2 className="h-4 w-4 text-muted-foreground" />
                {t("profiel.portaal_geluid")}
              </Label>
              <p className="text-[12.5px] text-muted-foreground mt-0.5">
                {t("profiel.portaal_geluid_uitleg")}
              </p>
            </div>
            <Switch
              id="portaal-geluid"
              checked={geluid}
              onCheckedChange={(aan) => {
                setGeluid(aan);
                zetPortaalGeluid(aan);
                // Meteen laten horen wat je aanzet; dit is bovendien de
                // klik die het autoplay-beleid van de browser vrijgeeft.
                if (aan) beluister("nieuw");
                else meldGebruikersinteractie();
              }}
            />
          </div>

          {geluid && (
            <div className="border-t pt-3 space-y-3">
              <div className="flex items-center gap-4">
                <Label
                  htmlFor="portaal-volume"
                  className="text-[13.5px] font-medium shrink-0"
                >
                  {t("profiel.portaal_volume")}
                </Label>
                <input
                  id="portaal-volume"
                  type="range"
                  min={0}
                  max={100}
                  step={5}
                  value={volume}
                  aria-valuetext={`${volume}%`}
                  className="flex-1 h-1.5 accent-brand-orange-600 cursor-pointer"
                  onChange={(e) => {
                    const waarde = Number(e.target.value);
                    setVolume(waarde);
                    zetPortaalVolume(waarde);
                  }}
                  // Slepen is ook een interactie; laat het resultaat
                  // meteen horen op de nieuwe stand.
                  onPointerUp={() => beluister("nieuw")}
                  onKeyUp={() => beluister("nieuw")}
                />
                <span className="text-[12.5px] tabular-nums text-muted-foreground w-9 text-right">
                  {volume}%
                </span>
              </div>

              <div>
                <p className="text-[12.5px] text-muted-foreground mb-2">
                  {t("profiel.portaal_tonen_uitleg")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {VOORBEELDEN.map((soort) => (
                    <Button
                      key={soort}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => beluister(soort)}
                    >
                      {t(`profiel.portaal_toon_${soort}`)}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
